import React from "react";
import { Button, Drawer } from "antd";
import {
  CloseOutlined,
  ClockCircleOutlined,
  CodeSandboxOutlined,
  PlayCircleOutlined,
  DeleteOutlined,
} from "@ant-design/icons";

const getMinutesAgo = (parkedTime) => {
  if (!parkedTime) return "";

  // Convert "YYYY-MM-DD HH:mm:ss" -> Date
  const parkedDate = new Date(parkedTime.replace(" ", "T"));
  const now = new Date();

  const diffMs = now - parkedDate;
  const diffMinutes = Math.floor(diffMs / (1000 * 60));

  if (diffMinutes < 1) return "Just now";
  if (diffMinutes < 60) return `${diffMinutes} min ago`;

  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours} hr ago`;

  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays} day ago`;
};

const ParkedOrderCard = ({ orderData, orderId, name, time, items, total, productList, currenciesList, retrieveParkedData, setParkedDrawerOpen, discardMessage }) => {
  return (
    <div
      style={{
        background: "#FFFFFF",
        border: "1px solid #E5E7EB",
        borderRadius: "12px",
        padding: "16px",
        marginBottom: "20px",
      }}
    >
      {/* Header Line */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <span style={{ fontSize: "0.95rem", color: "#0F0718", fontWeight: 500 }}>{orderId}</span>

          <span
            style={{
              background: "#FFE9C2",
              color: "#B27100",
              borderRadius: "6px",
              padding: "2px 8px",
              fontSize: "0.70rem",
              fontWeight: 600,
            }}
          >
            On Hold
          </span>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "0.75rem", color: "#9CA3AF" }}>
          <ClockCircleOutlined style={{ fontSize: "0.75rem", color: "#9CA3AF" }} />
          {getMinutesAgo(orderData.parkedTime)}
        </div>
      </div>

      {/* Item List Container */}
      <div
        style={{
          background: "#F9FAFB",
          borderRadius: "8px",
          padding: "5px",
          marginTop: "1vh",
          marginBottom: "14px",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.85rem", fontWeight: 500 }}>
          <span>{items} item(s)</span>
          <span style={{ fontWeight: 600 }}>
            {currenciesList[0].symbolRightSide === "Y" ? `${currenciesList[0].currSymbol} ` : ""}
            {total}
            {currenciesList[0].symbolRightSide === "N" ? ` ${currenciesList[0].currSymbol} ` : ""}
          </span>
        </div>

        <div
          style={{
            marginTop: "8px",
            fontSize: "0.80rem",
            color: "#0F0718",
            lineHeight: "1.3rem",
          }}
        >
          {productList.map((p, i) => (
            <div key={i}>
              {p.label}
              <span style={{ float: "right" }}>
                {currenciesList[0].symbolRightSide === "Y" ? `${currenciesList[0].currSymbol} ` : ""}
                {p.price}
                {currenciesList[0].symbolRightSide === "N" ? ` ${currenciesList[0].currSymbol} ` : ""}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Footer Buttons */}
      <div style={{ display: "flex", gap: "10px" }}>
        <Button
          type="primary"
          style={{
            background: "#2F3856",
            borderColor: "#2F3856",
            flexGrow: 1,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 6,
            height: "34px",
            borderRadius: "8px",
            fontSize: "0.82rem",
            fontWeight: 500,
          }}
          onClick={() => {
            retrieveParkedData(orderData);
            setParkedDrawerOpen(false);
          }}
        >
          <PlayCircleOutlined style={{ fontSize: "0.9rem" }} />
          Retrieve
        </Button>

        <Button
          danger={false}
          style={{
            borderRadius: "8px",
            border: "1px solid #E5E7EB",
            width: "38px",
            height: "34px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            background: "#FFFFFF",
          }}
          onClick={() => {
            discardMessage("Discard Sale", "Are you sure you want to discard the sale!", orderData);
          }}
          icon={<DeleteOutlined style={{ color: "#9CA3AF" }} />}
        />
      </div>
    </div>
  );
};

const ParkedOrdersDrawer = ({
  parkedDrawerOpen,
  setParkedDrawerOpen,
  storedParkedList,
  currenciesList,
  retrieveParkedData,
  discardMessage,
}) => {
  return (
    <Drawer
      title={null}
      placement="right"
      open={parkedDrawerOpen}
      onClose={() => setParkedDrawerOpen(false)}
      width="29%"
      styles={{ body: {
        padding: "24px",
        background: "#F7F8FC",
        display: "flex",
        flexDirection: "column",
        height: "100%",
      } }}
      styles={{ header: { display: "none" } }}
    >
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", marginBottom: "16px" }}>
        <div
          style={{
            width: 40,
            height: 40,
            borderRadius: 10,
            background: "#2F3856",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginRight: 12,
          }}
        >
          <CodeSandboxOutlined style={{ fontSize: 20, color: "white" }} />
        </div>

        <div style={{ flexGrow: 1 }}>
          <div style={{ fontSize: "1.2rem", fontWeight: 600, color: "#0F0718" }}>Parked Orders</div>
          <div style={{ fontSize: "0.85rem", color: "#9197A3" }}>{storedParkedList?.length} order(s) on hold</div>
        </div>

        <CloseOutlined style={{ fontSize: 18, cursor: "pointer" }} onClick={() => setParkedDrawerOpen(false)} />
      </div>

      {/* Scrollable card container */}
      <div
        style={{
          overflowY: "auto",
          paddingRight: "8px",
          flexGrow: 1,
        }}
      >
        {storedParkedList.length === 0 ? (
          <div style={{ textAlign: "center", color: "#9CA3AF", marginTop: "40px" }}>No parked orders</div>
        ) : (
          storedParkedList.map((order, index) => {
            const cart = order.parkedCart || {};
            const items = cart.items || [];
            return (
              <ParkedOrderCard
                orderData={order}
                key={order.parkedBillId}
                orderId={order.parkedDocNo}
                name={cart.customer?.name || ""}
                items={items.length}
                total={cart.total}
                productList={items.map((item) => ({
                  label: `${item.weight}x ${item?.productAliasName ? item?.productAliasName : item.name}`,
                  price: item.nettotal,
                }))}
                currenciesList={currenciesList}
                retrieveParkedData={retrieveParkedData}
                setParkedDrawerOpen={setParkedDrawerOpen}
                discardMessage={discardMessage}
              />
            );
          })
        )}
      </div>
    </Drawer>
  );
};

export default React.memo(ParkedOrdersDrawer);
